<template>
  <div class="cu">
    <!-- Hero: FAQ search first, then the account shortcut -->
    <section class="cu-hero">
      <div class="cu-wrap cu-hero-inner">
        <div class="cu-hero-main">
          <p class="cu-eyebrow">Contact us</p>
          <h1 class="cu-h1">
            How can we help<template v-if="firstName"
              >, {{ firstName }}</template
            >?
          </h1>
          <p class="cu-lede">
            Most answers are in our FAQs, and most changes are quickest in your
            account. If you still need us, our UK team is here.
          </p>
          <form class="cu-search" role="search" @submit.prevent="searchFaqs">
            <label for="cu-search-input" class="sr-only">Search our FAQs</label>
            <span class="cu-search-field">
              <CuIcon name="search" class="cu-search-icon" />
              <input
                id="cu-search-input"
                v-model="query"
                type="search"
                class="cu-search-input"
                placeholder="Search FAQs, e.g. “what lock do I need”"
              />
            </span>
            <button type="submit" class="cu-btn cu-btn--ink">Search</button>
          </form>
          <div class="cu-popular">
            <span class="cu-popular-label">Popular:</span>
            <nuxt-link
              v-for="faq in popularFaqs"
              :key="faq.anchor"
              :to="`/faq#${faq.anchor}`"
              class="cu-chip"
            >
              {{ faq.label }}
            </nuxt-link>
          </div>
        </div>

        <aside class="cu-account">
          <template v-if="policy">
            <div class="cu-account-head">
              <span class="cu-icon-tile cu-icon-tile--blue">
                <CuIcon name="bike" />
              </span>
              <div>
                <p class="cu-account-kicker">Your policy</p>
                <p class="cu-account-title">
                  {{ policyCover }} · {{ policy.code }}
                </p>
              </div>
            </div>
            <p class="cu-account-text">
              Change your cover, add a bike, update payment or make a claim in
              your account. It’s free online – changes by phone or post have a
              £10 admin fee.
            </p>
            <nuxt-link
              :to="policyLink"
              class="cu-btn cu-btn--primary cu-btn--block"
            >
              Go to my policy →
            </nuxt-link>
          </template>
          <template v-else>
            <div class="cu-account-head">
              <span class="cu-icon-tile cu-icon-tile--blue">
                <CuIcon name="bike" />
              </span>
              <div>
                <p class="cu-account-kicker">Already insured with us?</p>
                <p class="cu-account-title">Your account</p>
              </div>
            </div>
            <p class="cu-account-text">
              Change your cover, add a bike, update payment or make a claim in
              your account. It’s free online – changes by phone or post have a
              £10 admin fee.
            </p>
            <nuxt-link
              :prefetch="false"
              to="/dashboard"
              class="cu-btn cu-btn--primary cu-btn--block"
            >
              {{ isAuthenticated ? 'Go to my account' : 'Log in' }} →
            </nuxt-link>
          </template>
        </aside>
      </div>
    </section>

    <div class="cu-wrap">
      <!-- Self-serve tasks -->
      <section class="cu-section">
        <h2 class="cu-h2">What do you need help with?</h2>
        <div class="cu-tasks">
          <nuxt-link
            v-for="task in tasks"
            :key="task.title"
            :to="task.to"
            :prefetch="false"
            class="cu-card cu-task"
          >
            <span class="cu-icon-tile" :class="`cu-icon-tile--${task.tone}`">
              <CuIcon :name="task.icon" />
            </span>
            <span class="cu-task-text">
              <span class="cu-task-title">{{ task.title }}</span>
              <span class="cu-task-desc">{{ task.desc }}</span>
              <span class="cu-link cu-task-cta">{{ task.cta }} →</span>
            </span>
          </nuxt-link>
        </div>
      </section>

      <!-- Talk to our team -->
      <section class="cu-section">
        <h2 class="cu-h2">Talk to our team</h2>
        <p class="cu-sub">
          Have your policy number ready – it starts with PA and is on your
          Policy Schedule.
        </p>
        <div class="cu-channels">
          <div class="cu-card cu-channel cu-channel--dark">
            <span class="cu-icon-tile cu-icon-tile--dark">
              <CuIcon name="chat" />
            </span>
            <h3 class="cu-h3">Live chat</h3>
            <p class="cu-channel-text">
              Chat with our team without leaving the page. You’ll also find the
              chat button in the bottom corner of every page.
            </p>
            <p v-if="chatUnavailable" class="cu-channel-note" role="status">
              Chat isn’t available right now – please call or email us instead.
            </p>
            <button
              type="button"
              class="cu-btn cu-btn--white cu-btn--block cu-channel-btn"
              :disabled="chatWaiting"
              @click="openChat"
            >
              {{ chatWaiting ? 'Opening chat…' : 'Start a chat' }}
            </button>
          </div>

          <div class="cu-card cu-channel">
            <span class="cu-icon-tile cu-icon-tile--green">
              <CuIcon name="phone" />
            </span>
            <h3 class="cu-h3">Call us</h3>
            <p class="cu-phone">0800 888 6745</p>
            <p class="cu-channel-text">
              Monday to Friday, 9.00am – 5.00pm.<br />
              Calls may be recorded for training, compliance and fraud
              prevention.
            </p>
            <a
              href="tel:08008886745"
              class="cu-btn cu-btn--outline cu-btn--block cu-channel-btn"
            >
              Call now
            </a>
          </div>

          <div class="cu-card cu-channel">
            <span class="cu-icon-tile cu-icon-tile--blue">
              <CuIcon name="mail" />
            </span>
            <h3 class="cu-h3">Email us</h3>
            <dl class="cu-emails">
              <div
                v-for="email in emails"
                :key="email.address"
                class="cu-email"
              >
                <dt>{{ email.label }}</dt>
                <dd>
                  <a :href="`mailto:${email.address}`" class="cu-link">
                    {{ email.address }}
                  </a>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <!-- Claims -->
      <section class="cu-claim">
        <div class="cu-claim-main">
          <h2 class="cu-h2">Need to make a claim?</h2>
          <p class="cu-claim-text">
            The fastest way is through your account – you can upload evidence
            and follow your claim’s progress there.
          </p>
          <div class="cu-claim-actions">
            <nuxt-link
              :to="claimLink"
              :prefetch="false"
              class="cu-btn cu-btn--green"
            >
              Make a claim
            </nuxt-link>
            <nuxt-link to="/claim" class="cu-btn cu-btn--outline-green">
              What to expect
            </nuxt-link>
          </div>
          <p class="cu-claim-fallback">
            Can’t log in? Email
            <a href="mailto:claims@pedalsure.com">claims@pedalsure.com</a>
            or call <a href="tel:08008886745">0800 888 6745</a>.
          </p>
        </div>
        <ol class="cu-steps">
          <li v-for="(step, i) in claimSteps" :key="step.title" class="cu-step">
            <span class="cu-step-num" aria-hidden="true">{{ i + 1 }}</span>
            <span>
              <span class="cu-step-title">{{ step.title }}</span>
              <span class="cu-step-text">{{ step.text }}</span>
            </span>
          </li>
        </ol>
      </section>

      <!-- Complaints + post -->
      <section class="cu-section cu-pair">
        <div class="cu-card cu-pair-card">
          <h2 class="cu-h3">Not happy with our service?</h2>
          <p class="cu-pair-text">
            We want to put it right. Tell us by phone or email, or make a formal
            complaint at
            <a href="mailto:complaints@pedalsure.com" class="cu-link">
              complaints@pedalsure.com</a
            >. Please quote your policy number.
          </p>
          <nuxt-link to="/faq#complaints" class="cu-link">
            How we handle complaints →
          </nuxt-link>
        </div>
        <div class="cu-card cu-pair-card">
          <h2 class="cu-h3">Write to us</h2>
          <address class="cu-pair-text">
            Pedalsure No. 3 Limited<br />
            Suite 3 Dukes House<br />
            4–6 High Street, Windsor SL4 1LD
          </address>
        </div>
      </section>
    </div>

    <section class="cu-trust">
      <div class="cu-wrap">
        <SiteTrust variant="cards" />
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { buildSocialMeta } from '@/helpers/social-meta'
import {
  LIVE_STATUSES,
  canChangePolicy,
  coverLabel,
} from '@/helpers/policyCard'
import SiteTrust from '@/components/homepage/SiteTrust'

const CANONICAL_PATH = '/contact-us'

// How long "Start a chat" waits for Zendesk (GTM injects it late, see
// plugins/zendesk-messenger.js) before saying chat isn't available.
const CHAT_WAIT_MS = 8000

// 24px stroke icons.
const ICONS = {
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z', 'M20 20l-4-4'],
  bike: [
    'M5.5 17.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    'M18.5 17.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    'M5.5 14.5l3.5-6h6l3.5 6M9 8.5l3 6h-6.5M12 14.5l3-6M8 6h3',
  ],
  shield: [
    'M12 3.2l7 2.8v6c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6l7-2.8z',
    'M9 12l2.2 2.2L15.2 10',
  ],
  pencil: ['M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4z', 'M13.5 6.5l4 4'],
  card: ['M3.5 6.5h17v11h-17z', 'M3.5 10h17', 'M7 14.5h3'],
  cancel: [
    'M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17z',
    'M9 9l6 6M15 9l-6 6',
  ],
  chat: ['M20 12a7.5 7.5 0 0 1-10.9 6.7L4.5 20l1.3-4.6A7.5 7.5 0 1 1 20 12z'],
  phone: [
    'M6.5 4h3l1.5 3.7-2 1.4a11 11 0 0 0 5.9 5.9l1.4-2L20 14.5v3a2 2 0 0 1-2.2 2A15.5 15.5 0 0 1 4 6.2 2 2 0 0 1 6 4z',
  ],
  mail: ['M3.5 6h17v12h-17z', 'M3.5 6.5l8.5 6.5 8.5-6.5'],
}

const CuIcon = {
  functional: true,
  props: { name: { type: String, required: true } },
  render(h, { props, data }) {
    return h(
      'svg',
      {
        ...data,
        class: ['cu-icon', data.class, data.staticClass],
        attrs: {
          viewBox: '0 0 24 24',
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': 1.6,
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          'aria-hidden': 'true',
        },
      },
      ICONS[props.name].map((d) => h('path', { attrs: { d } }))
    )
  },
}

export default {
  name: 'ContactUs',
  components: { SiteTrust, CuIcon },
  data() {
    return {
      query: '',
      chatWaiting: false,
      chatUnavailable: false,
      popularFaqs: [
        { anchor: 'make-changes', label: 'How do I make changes to my cover?' },
        { anchor: 'lock-requirements', label: 'What lock do I need?' },
        { anchor: 'cancel', label: 'How do I cancel?' },
        {
          anchor: 'renewal-price-increase',
          label: 'Why has my renewal price gone up?',
        },
      ],
      emails: [
        {
          label: 'Questions and policy changes',
          address: 'cservice@pedalsure.com',
        },
        {
          label: 'Claims (if you can’t log in)',
          address: 'claims@pedalsure.com',
        },
        { label: 'Formal complaints', address: 'complaints@pedalsure.com' },
      ],
      claimSteps: [
        {
          title: 'Report theft to the police first',
          text: 'Get a crime reference number – you’ll need it for your claim.',
        },
        {
          title: 'Tell us within 30 days',
          text: 'For theft or damage. For an injury, tell us within 28 days of the accident.',
        },
        {
          title: 'Log in and choose Make a claim',
          text: 'Add your photos, receipts and lock details so we can settle it without delay.',
        },
      ],
    }
  },
  head() {
    const title = 'Contact Us | PedalSure'
    const description =
      'If you have any questions, feel free to call us between 9.00am and 5.00pm Monday - Friday!'
    return {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        ...buildSocialMeta({
          title,
          description,
          path: CANONICAL_PATH,
        }).meta,
      ],
    }
  },
  computed: {
    ...mapGetters({
      isAuthenticated: 'user/isAuthenticated',
      policies: 'policy/policies',
    }),
    // Only a real name — the email's local part reads oddly in a heading.
    firstName() {
      const name = (this.$store.getters['user/user']?.displayName || '').trim()
      return name.split(/\s+/)[0]
    },
    // The first live policy, if the visitor is logged in and has one.
    policy() {
      if (!this.isAuthenticated) return null
      return (
        (this.policies || []).find((p) => LIVE_STATUSES.includes(p.status)) ||
        null
      )
    },
    policyCover() {
      return coverLabel(this.policy)
    },
    policyLink() {
      return `/dashboard/policy/${this.policy.code}`
    },
    claimLink() {
      return this.policy
        ? `/dashboard/policy/${this.policy.code}/claim-intro`
        : '/dashboard'
    },
    tasks() {
      const p = this.policy
      return [
        {
          title: 'Make a claim',
          desc: 'Report theft or damage and follow your claim online.',
          cta: 'Start a claim',
          to: this.claimLink,
          icon: 'shield',
          tone: 'green',
        },
        {
          title: 'Change my cover',
          desc: 'Add a bike, change add-ons or update your bike’s details.',
          cta: 'Edit my policy',
          to:
            p && canChangePolicy(p)
              ? `/dashboard/policy-edit/${p.code}`
              : p
              ? this.policyLink
              : '/faq#make-changes',
          icon: 'pencil',
          tone: 'blue',
        },
        {
          title: 'Payments & renewal',
          desc: 'Update your card, see your renewal date or change how you pay.',
          cta: 'Manage payments',
          to: p ? this.policyLink : '/faq#payment-method',
          icon: 'card',
          tone: 'blue',
        },
        {
          title: 'Cancel my policy',
          desc: 'Full refund within 14 days if no claims have been made.',
          cta: 'How to cancel',
          to: '/faq#cancel',
          icon: 'cancel',
          tone: 'grey',
        },
      ]
    },
  },
  mounted() {
    if (this.$route.query.type === 'paid') {
      this.$modal.show('congrats-modal', {
        buttonText: 'Continue to home page',
        url: '/',
      })
    }
    if (this.isAuthenticated && !(this.policies || []).length) {
      this.$store.dispatch('policy/fetchPolicies', { limit: 10, offset: 1 })
    }
  },
  beforeDestroy() {
    clearInterval(this.chatPoll)
  },
  methods: {
    searchFaqs() {
      const q = this.query.trim()
      this.$router.push(q ? { path: '/faq', query: { q } } : '/faq')
    },
    // Zendesk is injected by GTM on the deferred `ps_deferred` event, so
    // early in a page view zE may not exist yet — wait a few seconds for it
    // (same as NavigationDashboard), and say so if it never arrives.
    openChat() {
      const open = () => {
        zE('messenger', 'show')
        zE('messenger', 'open')
      }
      this.chatUnavailable = false
      if (typeof zE === 'function') return open()
      this.chatWaiting = true
      const started = Date.now()
      this.chatPoll = setInterval(() => {
        if (typeof zE === 'function') {
          clearInterval(this.chatPoll)
          this.chatWaiting = false
          open()
        } else if (Date.now() - started > CHAT_WAIT_MS) {
          clearInterval(this.chatPoll)
          this.chatWaiting = false
          this.chatUnavailable = true
        }
      }, 250)
    },
  },
}
</script>

<style lang="scss" scoped>
.cu {
  color: $ps-ink;
  font-family: $family-regular;
}

.cu-wrap {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 640px) {
    padding: 0 16px;
  }
}

.cu-icon {
  width: 20px;
  height: 20px;
}

// --- Hero ---------------------------------------------------------------

.cu-hero {
  background: $ps-surface;
  border-bottom: 1px solid $ps-border-color;
  padding: 64px 0 56px;

  @media (max-width: 640px) {
    padding: 32px 0;
  }
}

.cu-hero-inner {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 48px 80px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.cu-eyebrow {
  font-size: $ps-text-label;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $ps-green;
  margin-bottom: 12px;
}

.cu-h1 {
  font-size: $ps-text-h1;
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: -0.03em;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    font-size: 32px;
  }
}

.cu-lede {
  font-size: $ps-text-body;
  line-height: 1.5;
  color: $ps-body-color;
  max-width: 34em;
  margin-bottom: 24px;

  @media (max-width: 640px) {
    font-size: $ps-text-small;
  }
}

.cu-search {
  display: flex;
  gap: 8px;
  max-width: 560px;
}

.cu-search-field {
  position: relative;
  flex: 1;
}

.cu-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: $ps-muted;
  pointer-events: none;
}

.cu-search-input {
  width: 100%;
  height: $ps-control-h-compact;
  padding: 0 14px 0 42px;
  border: 1px solid $ps-border-strong;
  border-radius: $ps-radius-sm;
  background: $ps-white;
  font: inherit;
  font-size: $ps-text-small;
  color: $ps-ink;

  &:focus {
    outline: none;
    border-color: $ps-primary;
    box-shadow: 0 0 0 3px $ps-primary-100;
  }
}

.cu-popular {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}

.cu-popular-label {
  font-size: $ps-text-label;
  color: $ps-muted;
}

.cu-chip {
  font-size: $ps-text-label;
  color: $ps-ink;
  background: $ps-white;
  border: 1px solid $ps-border-color;
  border-radius: $ps-radius-pill;
  padding: 6px 12px;

  &:hover {
    border-color: $ps-border-strong;
    color: $ps-ink;
  }
}

.cu-account {
  background: $ps-white;
  border: 1px solid $ps-border-color;
  border-radius: $ps-radius-md;
  box-shadow: 0 8px 24px -12px rgba(20, 22, 31, 0.15);
  padding: 20px;
}

.cu-account-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.cu-account-kicker {
  font-size: $ps-text-label;
  color: $ps-muted;
}

.cu-account-title {
  font-weight: 700;
  font-size: 15px;
}

.cu-account-text {
  font-size: 14px;
  line-height: 1.5;
  color: $ps-body-color;
  margin-bottom: 16px;
}

// --- Shared bits ----------------------------------------------------------

.cu-section {
  padding-top: 56px;

  @media (max-width: 640px) {
    padding-top: 40px;
  }
}

.cu-h2 {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 20px;

  @media (max-width: 640px) {
    font-size: 22px;
  }
}

.cu-sub {
  color: $ps-body-color;
  margin: -12px 0 20px;
}

.cu-h3 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}

.cu-card {
  background: $ps-white;
  border: 1px solid $ps-border-color;
  border-radius: $ps-radius-md;
  padding: 20px;
}

.cu-icon-tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: $ps-radius-sm;

  &--blue {
    background: $ps-primary-50;
    color: $ps-primary;
  }
  &--green {
    background: $ps-green-50;
    color: $ps-green;
  }
  &--grey {
    background: $ps-divider;
    color: $ps-body-color;
  }
  &--dark {
    background: rgba(255, 255, 255, 0.12);
    color: $ps-white;
  }
}

.cu-link {
  color: $ps-primary;
  font-weight: 700;

  &:hover {
    color: $ps-primary-hover;
    text-decoration: underline;
  }
}

.cu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: $ps-btn-h-compact;
  padding: 0 20px;
  border-radius: $ps-radius-sm;
  border: 1px solid transparent;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &--block {
    display: flex;
    width: 100%;
  }
  &--primary {
    background: $ps-primary;
    color: $ps-white;
    &:hover {
      background: $ps-primary-hover;
      color: $ps-white;
    }
  }
  &--ink {
    background: $ps-ink;
    color: $ps-white;
    height: $ps-control-h-compact;
    &:hover {
      background: #000;
    }
  }
  &--green {
    background: $ps-green;
    color: $ps-white;
    &:hover {
      background: $ps-green-hover;
      color: $ps-white;
    }
  }
  &--white {
    background: $ps-white;
    color: $ps-ink;
    &:hover {
      background: $ps-divider;
    }
    &:disabled {
      cursor: default;
      opacity: 0.8;
    }
  }
  &--outline {
    background: $ps-white;
    border-color: $ps-ink;
    color: $ps-ink;
    &:hover {
      background: $ps-surface;
      color: $ps-ink;
    }
  }
  &--outline-green {
    background: $ps-white;
    border-color: $ps-green;
    color: $ps-ink;
    &:hover {
      background: $ps-surface;
      color: $ps-ink;
    }
  }
}

// --- Tasks ----------------------------------------------------------------

.cu-tasks {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.cu-task {
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: $ps-ink;
  transition: border-color 0.15s;

  &:hover {
    border-color: $ps-border-strong;
    color: $ps-ink;

    .cu-task-cta {
      text-decoration: underline;
    }
  }

  @media (max-width: 640px) {
    flex-direction: row;
    align-items: center;
    padding: 14px 16px;
  }
}

.cu-task-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cu-task-title {
  font-weight: 700;
  font-size: 16px;
}

.cu-task-desc {
  font-size: 14px;
  line-height: 1.45;
  color: $ps-body-color;
}

.cu-task-cta {
  font-size: 14px;
  margin-top: 6px;

  @media (max-width: 640px) {
    display: none;
  }
}

// --- Channels -------------------------------------------------------------

.cu-channels {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.cu-channel {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .cu-icon-tile {
    margin-bottom: 14px;
  }

  &--dark {
    background: $ps-ink;
    border-color: $ps-ink;
    color: $ps-white;

    .cu-h3 {
      color: $ps-white;
    }
    .cu-channel-text {
      color: rgba(255, 255, 255, 0.78);
    }
  }
}

.cu-channel-text {
  font-size: 14px;
  line-height: 1.5;
  color: $ps-body-color;
  margin-bottom: 20px;
}

.cu-channel-note {
  font-size: 14px;
  color: $ps-gold;
  margin: -8px 0 16px;
}

.cu-channel-btn {
  margin-top: auto;
}

.cu-phone {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin-bottom: 8px;
}

.cu-emails {
  width: 100%;
}

.cu-email {
  padding: 10px 0;
  border-bottom: 1px solid $ps-divider;

  &:first-child {
    padding-top: 0;
  }

  dt {
    font-size: 12px;
    color: $ps-muted;
    margin-bottom: 2px;
  }
  dd {
    margin: 0;
    font-size: 15px;
    word-break: break-word;
  }
  .cu-link {
    text-decoration: underline;
  }
}

// --- Claim ----------------------------------------------------------------

.cu-claim {
  margin-top: 56px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px 40px;
  align-items: center;
  background: $ps-green-50;
  border-radius: $ps-radius-lg;
  padding: 36px 32px;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
  @media (max-width: 640px) {
    margin-top: 40px;
    padding: 24px 16px;
  }

  .cu-h2 {
    margin-bottom: 12px;
  }
}

.cu-claim-text {
  color: $ps-body-color;
  line-height: 1.5;
  margin-bottom: 20px;
}

.cu-claim-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.cu-claim-fallback {
  font-size: 14px;
  color: $ps-body-color;

  a {
    color: $ps-green-hover;
    font-weight: 700;
    text-decoration: underline;
  }
}

.cu-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cu-step {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: $ps-white;
  border-radius: $ps-radius-sm;
  padding: 14px 16px;
}

.cu-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: $ps-green;
  color: $ps-white;
  font-size: 13px;
  font-weight: 700;
}

.cu-step-title {
  display: block;
  font-weight: 700;
  font-size: 15px;
}

.cu-step-text {
  display: block;
  font-size: 14px;
  color: $ps-body-color;
}

// --- Complaints / post ------------------------------------------------------

.cu-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding-bottom: 56px;

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
    padding-bottom: 40px;
  }
}

.cu-pair-card {
  padding: 24px;

  > .cu-link {
    font-size: 15px;
  }
}

.cu-pair-text {
  font-size: 15px;
  font-style: normal;
  line-height: 1.5;
  color: $ps-body-color;
  margin-bottom: 12px;

  .cu-link {
    text-decoration: underline;
  }
}

// --- Trust ----------------------------------------------------------------

.cu-trust {
  background: $ps-surface;
  border-top: 1px solid $ps-border-color;
  padding: 48px 0;
}
</style>
